// =============================================================================
// Tendência de execução (migration 060, AF-01 §9)
//
// O AF pede "a evolução dos últimos meses por status" e dá o exemplo do aviso:
// "3 meses seguidos com excedente nesta categoria — considere solicitar revisão
// orçamentária". A contagem dos meses seguidos vem pronta do banco
// (`meses_seguidos_estouro`, que atravessa a virada do ano); aqui só se monta a
// faixa dos seis meses de cada categoria e se escolhe quem merece o aviso.
//
// ⚠️ O mês em andamento não quebra a sequência. A tela abre no mês corrente, e
// no começo do mês quase nada estourou ainda: contar a partir dele escondia
// justamente a categoria que estourou nos três meses fechados (achado na
// conferência da produção, em 14/10, com seis categorias nessa situação).
// =============================================================================

import type { FaixaTendencia, MesTendencia } from "./api.ts";
import { competenciaAnterior } from "./formato.ts";

/** A partir de quantos meses seguidos acima do orçado o aviso aparece. */
export const MESES_PARA_AVISO = 3;

/** O mês ainda aberto, no fuso da empresa: "2026-10-01". */
export function mesAbertoHoje(agora = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Sao_Paulo" }).format(agora).slice(0, 7) + "-01";
}

/** Os seis meses que terminam na referência, do mais antigo ao mais novo. */
export function seisMeses(referencia: string): string[] {
  const meses = [referencia];
  while (meses.length < 6) meses.unshift(competenciaAnterior(meses[0]!));
  return meses;
}

export type TendenciaCategoria = {
  chave: string;
  centro_custo_id: string;
  centro_custo_codigo: string;
  centro_custo_nome: string;
  categoria_id: string;
  categoria_nome: string;
  /** Meses seguidos acima do orçado. */
  seguidos: number;
  /** O mês em que a sequência termina: a referência, ou o mês anterior quando a referência ainda está aberta. */
  ate: string;
  /** Um por mês da janela, do mais antigo ao mais novo; mês sem dado é "sem_movimento". */
  faixas: { competencia: string; faixa: FaixaTendencia; pct: string | null }[];
};

/**
 * As categorias que fecham a referência com `minimo` meses seguidos ou mais
 * acima do orçado. Ordem: as que estouram há mais tempo primeiro.
 *
 * Com `mesAberto` igual à referência, o mês ainda em andamento só entra na
 * conta se já estourou; senão vale a sequência que fechou no mês anterior.
 */
export function tendenciasSeguidas(
  meses: MesTendencia[],
  referencia: string,
  { minimo = MESES_PARA_AVISO, mesAberto }: { minimo?: number; mesAberto?: string } = {},
): TendenciaCategoria[] {
  const janela = seisMeses(referencia);
  const porChave = new Map<string, MesTendencia[]>();
  for (const m of meses) {
    const chave = `${m.centro_custo_id}|${m.categoria_id}`;
    const lista = porChave.get(chave);
    if (lista) lista.push(m);
    else porChave.set(chave, [m]);
  }

  const saida: TendenciaCategoria[] = [];
  for (const [chave, lista] of porChave) {
    let fim = lista.find((m) => m.competencia === referencia);
    if (referencia === mesAberto && fim?.faixa !== "estouro") {
      const anterior = competenciaAnterior(referencia);
      fim = lista.find((m) => m.competencia === anterior);
    }
    if (!fim || fim.meses_seguidos_estouro < minimo) continue;
    saida.push({
      chave,
      centro_custo_id: fim.centro_custo_id,
      centro_custo_codigo: fim.centro_custo_codigo,
      centro_custo_nome: fim.centro_custo_nome,
      categoria_id: fim.categoria_id,
      categoria_nome: fim.categoria_nome,
      seguidos: fim.meses_seguidos_estouro,
      ate: fim.competencia,
      faixas: janela.map((c) => {
        const m = lista.find((x) => x.competencia === c);
        return { competencia: c, faixa: m?.faixa ?? "sem_movimento", pct: m?.pct_execucao ?? null };
      }),
    });
  }
  return saida.sort((a, b) =>
    b.seguidos - a.seguidos
    || a.centro_custo_codigo.localeCompare(b.centro_custo_codigo)
    || a.categoria_nome.localeCompare(b.categoria_nome, "pt-BR"));
}
